import { message } from "antd"
import i18n from "../i18n"

const HTTP_STATUS_UNAUTHORIZED = 401
const NOTIFIED_FLAG = "__apiErrorNotified"
const DEDUP_WINDOW_MS = 1000

let lastNotifiedContent = ""
let lastNotifiedAt = 0

/** テスト用: 通知デデュープ状態をリセットする */
export function resetApiErrorHandlerStateForTests(): void {
  lastNotifiedContent = ""
  lastNotifiedAt = 0
}

export type NotifyApiErrorOptions = {
  error?: unknown
  status?: number | null
  /** console.error 用のメッセージ（UI文言とは別） */
  logMessage?: string
  /**
   * true のとき UI 通知を出さない（呼び出し側が既に専用メッセージを出している場合）。
   * ログは出力する。
   */
  silent?: boolean
}

function isAlreadyNotified(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === "object" &&
      (error as Record<string, unknown>)[NOTIFIED_FLAG] === true
  )
}

function markNotified(error: unknown): void {
  if (!error || typeof error !== "object") {
    return
  }
  try {
    Object.defineProperty(error, NOTIFIED_FLAG, {
      value: true,
      configurable: true,
      enumerable: false,
      writable: true,
    })
  } catch {
    // freeze/seal されたオブジェクトでは代入も失敗し得るため握りつぶす
    try {
      ;(error as Record<string, unknown>)[NOTIFIED_FLAG] = true
    } catch {
      // ignore
    }
  }
}

/**
 * API エラーをログし、必要ならユーザーへ通知する共通ハンドラ。
 * 401 は clientFetch の unauthorizedHandler（ログイン誘導）に任せるため通知しない。
 */
export function notifyApiError(options: NotifyApiErrorOptions = {}): void {
  const { error, status, logMessage, silent } = options

  if (logMessage !== undefined) {
    if (error !== undefined) {
      console.error(logMessage, error)
    } else {
      console.error(logMessage)
    }
  } else if (error !== undefined) {
    console.error(error)
  } else if (status != null) {
    console.error(`API request failed with status ${status}`)
  }

  if (silent) {
    return
  }

  if (status === HTTP_STATUS_UNAUTHORIZED) {
    return
  }

  if (isAlreadyNotified(error)) {
    return
  }

  markNotified(error)

  const content = `${i18n.t("apiError.fetchFailed")} ${i18n.t("apiError.retryHint")}`
  const now = Date.now()
  // clientFetch 通知 + context catch 通知の二重表示を抑止
  if (content === lastNotifiedContent && now - lastNotifiedAt < DEDUP_WINDOW_MS) {
    return
  }
  lastNotifiedContent = content
  lastNotifiedAt = now
  message.error(content)
}

/** HTTP レスポンスが失敗のときに通知する（401 はスキップ）。 */
export function notifyHttpError(
  response: Pick<Response, "ok" | "status">,
  options: Omit<NotifyApiErrorOptions, "status"> = {}
): void {
  if (response.ok) {
    return
  }
  notifyApiError({ ...options, status: response.status })
}
